import React, { useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useGetPatientPrescriptionQuery, useGetUsersQuery } from "store";
import {
    Button,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Text,
    Box,
    Link as ChakraLink,
    Grid,
    GridItem,
    Table,
    Tbody,
    Tr,
    Td,
    Spinner,
    Center,
    Flex,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import moment from "moment";
import jsPDF from "jspdf";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import rxIcon from "assets/img/rx1.jpg";

const PrescriptionDetails = () => {
    const { patientId, presId } = useParams();
    const {
        data: prescription,
        error,
        isLoading,
    } = useGetPatientPrescriptionQuery({
        patientId,
        presId,
    });
    const navigate = useNavigate();

    const handleUpdateClick = (patientId, presId) => {
        if (patientId && presId) {
            navigate(`/patients/${patientId}/prescriptions/${presId}/update`);
        } else {
            console.error("Invalid patientId or presId");
        }
    };

    const Day = moment(prescription?.date, "YYYY,MM,DD").toDate();

    const formattedDay = Day.toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    const time = prescription?.time;
    const formattedTime = moment(time, "HH:mm:ss.SSSSSS").format("h:mm A");

    // Fetch users data
    const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery();

    const userMap = useMemo(() => {
        const map = {};
        if (users && users.results) {
            users.results.forEach((user) => {
                if (user.id && user.username) {
                    map[user.id] = user.username;
                }
            });
        }
        return map;
    }, [users]);

    const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return "Unknown"; // Handle cases where the date_of_birth is missing
        const birthDate = moment(dateOfBirth);
        const today = moment();
        return today.diff(birthDate, "years"); // Calculate the difference in years
    };

    const generatePDF = (prescription) => {
        const signature = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAALoAAABLBAMAAAA17oB+AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAtUExURf///5mZmURERO7u7qqqqgAAADMzMxkZGd3d3WZmZlVVVczMzIiIiHd3d7u7uwk2SxEAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAPmSURBVFjD7ZfPaxNBFMcn2TbZNO0SWlbqD8SDa8AfkCKhlNISKTmoVWqa6Zq0KUVIg4VCW9nGRiupupQeKgEN9SJUNFQKQi69eJAerNBLCUqw6KWgQUrxf3B+7Ga3v5uYvcg+yO7MsPnsd95782YWANNMM80000wzzRCr9xgIr+oxkm4dMRAOvC58nVlcbDSC7s+iSy2Mx+E7A+gL+BJ9DsAsXK44nI1g6UHcbAlXPL6ObnSRU6TNP6g03T6PLgIV7ZSSFaYXNhD1ltKZHqgwfcWjS3kuXGH6OfRbcqk9/zx4tvqqUYsu+2/0BPrli7SWwAIUJdjzQ4VL9/HNkigPzmKXD6qdz1CKnPaAp18gTSIwPRrAt3T0TFl0y210idA2J/SsNfWRZluATkdI8km8JrhAWXQbyhJLL2nOSg8RKe8jHT9JfS4IZBQTaxfgXeXQq3NqyszAs1ivtZ+MOyVcfqZHSJHzu0BNfzl0GUn14vrSAtfpSDRLbs3olUw8SbS7UT9eDh0vJvwGkHmp+uoeFR8CoOY2KdAsjoVSLJC3ho9O5z20BteqyxWwIXrPZ4mzkfYaXIocQVVP8Oj0bx46cX+qOJSnrqlOgXFAtMs53I8p0qUSkvMOoBOPaUPN1DW2AQ6rRNoXPKoPkf2GJVS6hJLyC9oQS4tNbTeJANJOXWUnmeWEJTgGTCCP4r8VsrpYkD2K6yUukl0s5XFkna3A1J4cdmsfug07slrnzRqyupgxsoS9riq62EAGOcgRiu/ev5hfV+MS9O1N9+KgWXQTZihBIK6QXQ6lPqPgOoXOnauq/Tja78Xz7qG9wpFQ0h0IuncvkdkLy1S7XZlWXehU+pE/p/936w03Ir/fSu5TqZF2nrzVqhNP9loQ81DtvBqSNIzothfm46obwqG1g1II0eeUcqidN0iOOinI6xsv8i4DuRieP1Eo3v10SHZOkJQnsQxrj+KC76B0uaNvV0QAM5WBwzcPP58g9KTS5EPFx7Gz7Ir2673a0zYS0/bvMTjRsQ+Q0+fOHHCqDmfToyrei8LK03G5s0t7WsAHiBOSeOnkfmrbBH2h4D1kqVK8MNagaERJmKGavfXa47jUbUriiwNc4h9PbqPrTtjcnPj6J24UUN5l6NmmOapNlU8xx8S3B0aydVuv4PPqM/iDJMYvXnGLqHmNJqJDLCq1hKcEsQGUYNW5lW1a2M1FCSbWtQFHcb9m0j1wOHt0NKbPj+8cYp7oe47itmKFgVI/IOrEQ7Zj51elwUUjpZ9hBzeO9lyTEKr0AVmzKilg4KdhGr4xDv4YXjAODgqTRn4yO42LqGmmmfZ/2l/GpdmVJAYFgwAAAABJRU5ErkJggg=="

        const doc = new jsPDF({ unit: "pt", format: "letter" });
        const pageWidth = doc.internal.pageSize.getWidth();
        let y = 40; // start Y position

        // ——— 1) Header: Clinic name & address ———
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text("Central Medical Clinic", pageWidth / 2, y, {
            align: "center",
        });
        y += 22;
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.text("Paul’s Avenue, Kingstown", pageWidth / 2, y, {
            align: "center",
        });
        y += 16;
        doc.text("St. Vincent", pageWidth / 2, y, { align: "center" });
        y += 16;
        doc.text(
            "Tel.: (784) 457-2603   Fax: (784) 457-1173",
            pageWidth / 2,
            y,
            { align: "center" }
        );

        // horizontal divider
        y += 20;
        doc.setLineWidth(0.5);
        doc.line(40, y, pageWidth - 40, y);

        // ——— 2) Date/time block ———
        y += 20;
        const dateStr = dayjs().format("MMM D, YYYY, h:mm a");
        doc.setFont("helvetica", "bold");
        doc.text(`Date: ${dateStr}`, pageWidth - 40, y, { align: "right" });

        // ——— 3) Patient info ———
        y += 30;
        doc.setFont("helvetica", "bold");
        doc.text("NAME:", 40, y);
        doc.text(
            prescription.patient_details.name +
                " " +
                prescription.patient_details.surname,
            110,
            y
        );

        y += 18;
        doc.text("ADDRESS:", 40, y);
        doc.text(prescription.patient_details.address || "N/A", 110, y);

        y += 18;
        // calculate age
        const dob = prescription.patient_details.date_of_birth;
        const age = dob ? dayjs().diff(dayjs(dob), "year") : "N/A";
        doc.text(`Age: ${age} years old`, pageWidth - 40, y, {
            align: "right",
        });

        

        // — Prescription Icon (PNG)
        y += 30;
        doc.addImage(rxIcon, "JPEG", 40, y - 8, 24, 24);

        // ——— 4) Notes / Medications ———
        y += 30;
        doc.setFont("helvetica", "bold").setFontSize(12);
        doc.text("Notes:", 40, y);

        // wrap the long notes string
        const notes = prescription.notes || "";
        const splitNotes = doc.splitTextToSize(notes, pageWidth - 80);
        doc.text(splitNotes, 100, y);

        // ——— 5) Repeat line ———
        y += splitNotes.length * 14 + 20;
        doc.setFont("helvetica", "bold");
        doc.text(`Repeat: ${prescription.repeat ? "Yes" : "No"}`, 40, y);

        // ——— 6) Footer / signature placeholder ———
        y += 50;
        const sigWidth = 120;
        const centerX = pageWidth / 2;

        // compute starting X so the line and image sit centered
        const sigX = centerX - sigWidth / 2;

        // draw a line for the signature
        doc.setLineWidth(0.5).line(sigX, y, sigX + sigWidth, y);

        // draw the signature image just above the line
        doc.addImage(signature, "PNG", sigX, y - 40, sigWidth, 40);

        // doctor’s printed name centered under the line
        doc.setFont("helvetica", "normal");
        doc.text("Dr. Roger Duncan M.D., MPH, MSc.", centerX, y + 15, {
            align: "center",
        });

        // finally save
        doc.save("prescription.pdf");
    };

    if (isLoading) {
        return (
            <Center>
                <Spinner size="xl" />
            </Center>
        );
    }

    if (error) {
        return <Text> Error fetching user details: {error.message}</Text>;
    }

    return (
        <>
            <Helmet>
                <title>Prescription Details</title>
            </Helmet>
            <Box mt={20} p={4}>
                <Card
                    boxShadow="md"
                    borderRadius="2xl"
                    overflow="hidden"
                    p={4}
                    border="1px solid"
                    borderColor="gray.300"
                >
                    <CardHeader borderBottom="1px solid" borderColor="blue.200">
                        <Flex
                            w="100%"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box flex="1" textAlign="center">
                                <Text fontSize="2xl" fontWeight="bold">
                                    {prescription.patient_details?.name}{" "}
                                    {prescription.patient_details?.surname}{" "}
                                    Details
                                </Text>
                            </Box>
                            <Button onClick={() => generatePDF(prescription)}>
                                Generate PDF
                            </Button>
                        </Flex>
                    </CardHeader>

                    <CardBody>
                        <Box
                            overflow="auto"
                            border="1px solid"
                            borderRadius="md"
                            borderColor="gray.300"
                        >
                            <Table
                                variant="striped"
                                colorScheme="blue"
                                size="md"
                                borderRadius="md"
                                overflow="hidden"
                            >
                                <Tbody>
                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Patient Name</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {prescription.patient_details?.name}
                                        </Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Patient Surname</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {
                                                prescription.patient_details
                                                    ?.surname
                                            }
                                        </Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Patient Age</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {prescription.patient_details
                                                .date_of_birth
                                                ? calculateAge(
                                                      prescription
                                                          .patient_details
                                                          ?.date_of_birth
                                                  )
                                                : "N/A"}
                                        </Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Patient Address</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {
                                                prescription.patient_details
                                                    .address
                                            }
                                        </Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Date</strong>
                                        </Td>
                                        <Td fontSize="md">{formattedDay}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Time</strong>
                                        </Td>
                                        <Td fontSize="md">{formattedTime}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Repeat</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {prescription.repeat ? "Yes" : "No"}
                                        </Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Notes</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {prescription.notes}
                                        </Td>
                                    </Tr>

                                    {isLoadingUsers ? (
                                        <Tr>
                                            <Td fontSize="md">
                                                <strong>Enter By</strong>
                                            </Td>
                                            <Td fontSize="md">
                                                <Spinner size="sm" />{" "}
                                                {/* Spinner to indicate loading */}
                                            </Td>
                                        </Tr>
                                    ) : (
                                        <Tr>
                                            <Td fontSize="md">
                                                <strong>Entered By</strong>
                                            </Td>
                                            <Td fontSize="md">
                                                {/* Use the userMap to display the username, or show the ID if not found */}
                                                {userMap[
                                                    prescription.enter_by
                                                ] || prescription.enter_by}
                                            </Td>
                                        </Tr>
                                    )}
                                </Tbody>
                            </Table>
                        </Box>
                    </CardBody>

                    <CardFooter
                        p={4}
                        borderTop="1px solid"
                        borderColor="gray.300"
                    >
                        <Grid
                            templateColumns="repeat(2, 1fr)"
                            gap={4}
                            width="100%"
                        >
                            <GridItem>
                                <ChakraLink
                                    as={Link}
                                    to={`/patients/${patientId}/prescriptions`}
                                    color="blue.500"
                                >
                                    Back to List
                                </ChakraLink>
                            </GridItem>
                            <GridItem textAlign="right">
                                <Button
                                    colorScheme="blue"
                                    onClick={(e) => {
                                        handleUpdateClick(patientId, presId);
                                        e.stopPropagation();
                                    }}
                                >
                                    Update
                                </Button>
                            </GridItem>
                        </Grid>
                    </CardFooter>
                </Card>
            </Box>
        </>
    );
};

export default PrescriptionDetails;
